import React, { useContext, useEffect, useRef, useState } from 'react'
import { AppContext } from '@state/context'
import { useLocation } from 'react-router-dom'
import { Logo } from '@components/Svg'
import Button from '@components/Button'
import NavbarLink from '@components/NavbarLink'
import { Link } from 'react-router-dom'
import {
  BUTTON_SIZE,
  BUTTON_TYPE,
  COLORS,
  CUSTOMER_ACTIONS,
  PANELS,
  TEXT_SIZES,
  USER_ROLES,
} from '@helpers/constants'
import {
  NavigationContainer,
  LogoSection,
  ButtonSection,
  LinkSection,
  SectionsWrapper,
} from './style'
import Menu from '@components/Menu'
import MenuItem from '@components/MenuItem'
import { openPanel } from '@state/actions/panelsActions'
import Text from '@components/Text'
import { CheckMark, CloseMedium } from '@components/Svg/SVGIcons'
import withTooltip from '@components/WithTooltip'
import Tooltip from '@components/Tooltip'
import { useCustomerAction } from '@hooks/useCustomerAction'
import { useCurrentUser } from '@hooks/useCurrentUser'
import { useUpdateCurrentUser } from '@hooks/mutations/useUpdateCurrentUser'
import { useOwnBooksAllowed } from '@hooks/useOwnBooksAllowed'

const ButtonWithTooltip = withTooltip(Button)

const handleLogout = () => {
  axios.post('/logout').then(() => (location.href = '/login#'))
}

const Header = () => {
  const [state, dispatch] = useContext(AppContext)
  const {
    app: { userRole, shownTooltips },
  } = state
  const { currentUser } = useCurrentUser()
  const { showTooltips } = currentUser ?? {}
  const ownBooksAllowed = useOwnBooksAllowed()
  const [showHelpTooltip, setShowHelpTooltip] = useState(false)
  const helpTooltipRef = useRef()
  const { post: postUpdateCurrentUser } = useUpdateCurrentUser()
  const { trackCustomerAction } = useCustomerAction()

  const { pathname } = useLocation()
  const activeRoute = pathname.split('/')[1]

  const onCloseTooltip = () => {
    setShowHelpTooltip(false)
    trackCustomerAction(CUSTOMER_ACTIONS.CLOSED_HELP_TOOLTIP)
  }

  useEffect(() => {
    if (currentUser && !currentUser.hasClosedHelpTooltip && activeRoute !== '' && shownTooltips >= 3) {
      setTimeout(() => {
        setShowHelpTooltip(true)
      }, 3000)
    }
  }, [currentUser, activeRoute, shownTooltips])

  return (
    <>
      <nav className={NavigationContainer}>
        <div className={SectionsWrapper}>
          <div className={LogoSection}>
            <Link to={'/'}>
              <Logo />
            </Link>
          </div>
          <div className={ButtonSection}>
            {(userRole === USER_ROLES.ADMIN ||
              userRole === USER_ROLES.TEACHER) && (
              <>
                <ButtonWithTooltip
                  type={BUTTON_TYPE.PRIMARY}
                  size={BUTTON_SIZE.LARGE}
                  to={'/lararrum'}
                  icon={'Classes'}
                  active={activeRoute === 'lararrum'}
                  margin
                  iconLeft
                  tooltip={'Dina klasser och grupper.'}
                >
                  Mitt lärarrum
                </ButtonWithTooltip>
                <ButtonWithTooltip
                  type={BUTTON_TYPE.PRIMARY}
                  size={BUTTON_SIZE.LARGE}
                  to={'/bibliotek/bocker'}
                  icon={'Library'}
                  active={activeRoute === 'bibliotek'}
                  margin
                  iconLeft
                  tooltip={
                    ownBooksAllowed ? 'Alla böcker och avsnitt.' : 'Alla böcker.'
                  }
                >
                  Bibliotek
                </ButtonWithTooltip>
                <ButtonWithTooltip
                  type={BUTTON_TYPE.PRIMARY}
                  size={BUTTON_SIZE.LARGE}
                  to={'/bokhylla/bocker'}
                  icon={'Favourite'}
                  active={activeRoute === 'bokhylla'}
                  margin
                  iconLeft
                  tooltip={
                    ownBooksAllowed
                      ? 'Sparade böcker/avsnitt och dina egna böcker.'
                      : 'Sparade böcker och böcker delade med klasser.'
                  }
                >
                  Min bokhylla
                </ButtonWithTooltip>
              </>
            )}

            {userRole === USER_ROLES.STUDENT && (
              <Button
                type={BUTTON_TYPE.PRIMARY}
                size={BUTTON_SIZE.LARGE}
                to={'/bokhylla'}
                icon={'Favourite'}
                active={activeRoute === 'bokhylla'}
                margin
                iconLeft
              >
                Bokhylla
              </Button>
            )}
          </div>
          <div className={LinkSection}>
            <div className={'flex items-center'}>
              {(userRole === USER_ROLES.ADMIN ||
                userRole === USER_ROLES.TEACHER) && (
                <>
                  <div className={'flex'}>
                    <Button
                      size={BUTTON_SIZE.NO_PADDING}
                      textColor={COLORS.WHITE.TEXT}
                      handleClick={() => {
                        dispatch(openPanel(PANELS.SEARCH))
                      }}
                      icon={'Search'}
                      iconLeft
                    >
                      Sök
                    </Button>
                  </div>

                  <div ref={helpTooltipRef}>
                    <Menu title='Hjälp'>
                      <MenuItem
                        href='/app#/hjalp'
                        title='Hjälp'
                        target='_self'
                      />
                      <MenuItem
                        title='Tips'
                        onClick={() => postUpdateCurrentUser({ id: currentUser?.id, showTooltips: !showTooltips }, false)}
                      >
                        <div className={`flex items-center mt-[-4px] mb-[-4px] justify-center ${showTooltips ? 'text-primaryGreen' : 'text-lightDarkGrey'}`}>
                          <Text extraClasses='mr-1' color={showTooltips ? COLORS.PRIMARY_GREEN.TEXT : COLORS.LIGHT_DARK_GREY.TEXT}>{showTooltips ? 'På' : 'Av'}</Text>
                          {
                            showTooltips ?
                            <CheckMark classes={'w-[1.25rem] h-[1.25rem]'}/>
                            :
                            <CloseMedium classes={'w-[1.25rem] h-[1.25rem]'}/>
                          }
                        </div>
                      </MenuItem>
                    </Menu>
                  </div>

                  {userRole !== USER_ROLES.ADMIN &&
                    <Tooltip
                      referenceElement={helpTooltipRef.current}
                      show={showHelpTooltip}
                      color={COLORS.LIGHT_RED}
                      maxWidth={'300px'}
                      offset={[0, 10]}
                    >
                      <Text extraClasses='mb-2'>
                        Visa eller dölj vägledande tips.
                      </Text>
                      <div className='ml-auto'>
                        <Button handleClick={onCloseTooltip} type={BUTTON_TYPE.PRIMARY}><span className={'mx-4'}>Ok</span></Button>
                      </div>
                    </Tooltip>
                  }

                  <Menu title='Profil'>
                    <MenuItem
                      href='/app#/profil'
                      title='Inställningar'
                      target='_self'
                    />
                    <MenuItem
                      href='https://doklet.se/anvandarvillkor-integritetspolicy/'
                      title='Användarvillkor & integritetspolicy'
                      target={'_blank'}
                    />
                    <MenuItem title='Logga ut' onClick={handleLogout} />
                  </Menu>
                </>
              )}

              {userRole === USER_ROLES.STUDENT && (
                <NavbarLink
                  to={'#'}
                  size={TEXT_SIZES.XXSMALL}
                  handleClick={handleLogout}
                >
                  Logga ut
                </NavbarLink>
              )}
            </div>
          </div>
        </div>
      </nav>
    </>
  )
}

export default Header
